import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { bindActionCreators, compose } from "redux";
import _ from "lodash";
import { LabeledButtonWithCount } from "@loanbossinc/component-library";
import CircularProgress from "@material-ui/core/CircularProgress";
import FolderIcon from "@material-ui/icons/Folder";
import { CardPageLayout } from "component-lib/CardPageLayout";
import { GenericHeroCard } from "component-lib/";

import { dealGet } from "../../deal/deal.store";
import calcIcon from "../calcIcon.svg";
import LenderReportingDrawer from "../drawer";

import { actionCreators } from "./dashboard.store";
import { actionCreators as fieldActionCreators } from "../drawer/field-management/fieldManagement.store";
import { actionCreators as sourceFileActionCreators } from "../drawer/source-file-management/sourceFileManagement.store";
import { actionCreators as drawerActionCreators } from "../drawer/lenderReportingDrawer.store";
import ReportConfigListItem from "./reportConfigListItem";
import { Container, Header, LabeledIconWrapper, StyledBodyWrapper, StyledReportWrapper } from "./styles";
import { BackButtonText } from "pages/lenderReportingDashboard/constants";
import {compareFrequency} from "../../lenderReportingDashboard/lenderReportingDashboard.helper";

const LenderReportingDashboard = props => {
  const {
    deal,
    fieldCount,
    getFieldCountForDeal,
    getReportGroupsForDeal,
    getSourceFileCountForDeal,
    history,
    initialDrawerSectionName,
    isLenderReportingDrawerOpen,
    allDealLoans,
    match: {
      params: { dealId }
    },
    isGuest,
    isLoading,
    reportConfigs,
    setDashboardSelections,
    sourceFileCount,
    getAllLoansForDeal,
    setDashboardSelectionsToDefault,
    setFieldSelections,
    clearSourceFileManagementSelections,
    setDrawerToDefault
  } = props;
  const currentDate = new Date();

  useEffect(() => {
    setFieldSelections({
      fields: null,
      isAdding: false,
      isEditing: false,
      currentSelectedFieldType: null,
      isFromAddNewLineItem: false,
      isFromDebtYieldCalculation: false,
      isSelectingField: false,
      selectedField: null,
      noiValuesPerReportGroup: null
    });
    clearSourceFileManagementSelections();
    setDrawerToDefault();
  }, [setFieldSelections, clearSourceFileManagementSelections, setDrawerToDefault]);

  useEffect(() => {
    props.dealGet({ dealId });
  }, [dealId]);
  
  useEffect(() => {
    if (!_.isEmpty(deal) && !reportConfigs) {
      getReportGroupsForDeal({ dealId });
    }
  }, [dealId, deal, reportConfigs]);

  useEffect(() => {
    if (!_.isEmpty(deal) && !allDealLoans) {
      getAllLoansForDeal({ dealId });
    }
  }, [dealId, deal, allDealLoans]);

  useEffect(() => {
    if (Boolean(dealId) || !isLenderReportingDrawerOpen) {
      getFieldCountForDeal({ dealId });
      getSourceFileCountForDeal({ dealId });
    }
  }, [dealId, isLenderReportingDrawerOpen]);
  
  useEffect(() => {
      setDashboardSelectionsToDefault();
    },[]);
  
  useEffect(()=>{
    if(_.isEmpty(deal) || isGuest) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.has("action") && params.get("action") === "focus-pms-drawer") {
      setDashboardSelections({ isLenderReportingDrawerOpen: true, initialDrawerSectionName: "source-file-management" });
    }
  },[deal,sourceFileCount]);

  const heroCard = useCallback(() => {
    if (!deal) {
      return null;
  }

    return (
      <div>
        <GenericHeroCard
          {...props}
          deal={deal}
          title={deal.name}
          backLabel="Deal Summary"
          hideBackButton
          onBackButtonClick={() => history.push(`/deal/${dealId}/`)}
        />

        {!isGuest && (
          <LabeledIconWrapper>
            <Container>
              <LabeledButtonWithCount
                label="Source Files"
                icon={<FolderIcon />}
                count={sourceFileCount}
                style={{ width: "100%", borderTopRightRadius: 0, borderBottomRightRadius: 0, backgroundColor: "#fff" }}
                onClick={() => setDashboardSelections({ isLenderReportingDrawerOpen: true, initialDrawerSectionName: "source-file-management" })}
              />
              <LabeledButtonWithCount
                label="Fields"
                icon={<img src={calcIcon} alt="Field Icon" height={18} width={18} />}
                count={fieldCount}
                style={{ width: "100%", borderTopLeftRadius: 0, borderBottomLeftRadius: 0, backgroundColor: "#fff" }}
                onClick={() => setDashboardSelections({ isLenderReportingDrawerOpen: true, initialDrawerSectionName: "field-management" })}
              />
            </Container>
          </LabeledIconWrapper>
        )}
      </div>
    );
  }, [deal, isGuest, sourceFileCount, fieldCount]);

  const getReportList = useCallback(
    () => reportConfigs.sort(compareFrequency).map(
      reportConfig => reportConfig && (
        <ReportConfigListItem
          key={reportConfig.id}
          id={reportConfig.id}
          reportName={reportConfig.reportName}
          startDate={reportConfig.firstReportDueDate}
          lastPublishedDate={reportConfig.updatedAt}
          shouldCalculateDscr={reportConfig.shouldCalculateDscr}
          shouldCalculateDebtYield={reportConfig.shouldCalculateDebtYield}
          allDealLoans={allDealLoans}
          dealId={dealId}
          history={history}
          onClick={() => {
            if (reportConfig.reportGroupInstances && reportConfig.reportGroupInstances.length) {
              const sortedRgis = _.sortBy(reportConfig.reportGroupInstances.filter(x => x.isDraft !== true), ["dueDate"]);
              const currentRgi = sortedRgis.find(x => new Date(x.dueDate) > currentDate);
              if (currentRgi) {
                history.push(`/lenderReportingDashboard/tasks/${currentRgi.id}`, { from: BackButtonText.LENDER_REPORTS.FROM });
              } else {
                const reverseSortedRgis = _.reverse(sortedRgis);
                history.push(`/lenderReportingDashboard/tasks/${(reverseSortedRgis.find(x => new Date(x.dueDate) < currentDate)).id}`, { from: BackButtonText.LENDER_REPORTS.FROM });
              }
            }
          }}
        />
      )
    ),
    [reportConfigs, allDealLoans]
  );

  const closeDrawer = () => setDashboardSelections({ isLenderReportingDrawerOpen: false, initialDrawerSectionName: null });

  const body = useCallback(() => {
    if (!reportConfigs || isLoading || !deal || !allDealLoans) {
      return (
        <StyledBodyWrapper style={{ alignItems: "center" }}>
          <CircularProgress size={40} style={{ color: "#005AA9" }} />
        </StyledBodyWrapper>
      );
    }

    return (
      <StyledBodyWrapper>
        {!reportConfigs?.length ? (
          <>No reports</>
        ) : (
          <>
              <Header>Lender Compliance Reports</Header>
              <StyledReportWrapper>
                {reportConfigs && Boolean(reportConfigs.length) && getReportList()}
              </StyledReportWrapper>
          </>
        )}
      </StyledBodyWrapper>
    );
  }, [reportConfigs, isLoading, deal, allDealLoans]);

  return (
    <>
      <CardPageLayout {...props} isReversed bannerImageUrl={deal && deal.imageLarge} cardComponent={heroCard()} bodyComponent={body()} />
      <LenderReportingDrawer
        authentication={props.authentication}
        closeDrawer={closeDrawer}
        deal={deal}
        allDealLoans={allDealLoans}
        initialDrawerSectionName={initialDrawerSectionName}
        isOpen={isLenderReportingDrawerOpen}
      />
    </>
  );
};

const mapStateToProps = ({ lenderReportList, newDeal: { deal }, authentication: { user, isGuest } }) => ({ deal, user, isGuest, ...lenderReportList });
const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ dealGet, ...actionCreators, ...fieldActionCreators, ...sourceFileActionCreators, ...drawerActionCreators }, dispatch);

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(LenderReportingDashboard);
